import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList,
  Loader2,
  CheckCircle2,
  PackageCheck,
  Wallet,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import { api, formatRp, statusMeta, getStatusDisplay, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="mt-2 font-display text-3xl font-bold text-slate-900">{value}</div>
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

export default function DashboardPage() {
  const { user } = useAuth();
  const showRevenue = can.viewRevenue(user?.role);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/dashboard/stats"), api.get("/services")])
      .then(([s, r]) => {
        setStats(s.data);
        setRecent(r.data.slice(0, 6));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const c = stats?.counts || {};

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Dashboard
        </h1>
        <p className="text-sm text-slate-500 mt-1">Ringkasan aktivitas service Anda</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          testid="stat-masuk"
          icon={ClipboardList}
          label="Barang Masuk"
          value={c.masuk || 0}
          tone="bg-amber-100 text-amber-700"
        />
        <StatCard
          testid="stat-proses"
          icon={Loader2}
          label="Dalam Proses"
          value={c.proses || 0}
          tone="bg-blue-100 text-blue-700"
        />
        <StatCard
          testid="stat-selesai"
          icon={CheckCircle2}
          label="Selesai"
          value={c.selesai || 0}
          tone="bg-emerald-100 text-emerald-700"
        />
        <StatCard
          testid="stat-diambil"
          icon={PackageCheck}
          label="Diambil"
          value={c.diambil || 0}
          tone="bg-slate-200 text-slate-700"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {showRevenue && (
          <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <Wallet className="w-4 h-4 text-blue-600" />
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                Pendapatan Bulan Ini
              </span>
            </div>
            <div className="font-display text-3xl font-bold text-slate-900" data-testid="revenue-month">
              {formatRp(stats?.revenue_month)}
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500">Total Semua Waktu</div>
                <div className="font-semibold text-slate-800">{formatRp(stats?.revenue_total)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Bulan Ini</div>
                <div className="font-semibold text-slate-800">{stats?.this_month_total} order</div>
              </div>
            </div>
          </div>
        )}

        <div className={`${showRevenue ? "lg:col-span-2" : "lg:col-span-3"} bg-white border border-slate-200 rounded-xl p-5 shadow-sm`}>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              Tren 6 Bulan Terakhir
            </span>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={stats?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                <YAxis stroke="#94a3b8" fontSize={11} />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="incoming" name="Masuk" fill="#2563EB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="completed" name="Selesai" fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 flex items-center justify-between border-b border-slate-100">
          <div>
            <h3 className="font-display font-semibold text-slate-900">Barang Terbaru</h3>
            <p className="text-xs text-slate-500 mt-0.5">6 order paling baru</p>
          </div>
          <Link to="/services">
            <Button variant="outline" size="sm" className="gap-1" data-testid="see-all-services">
              Lihat semua <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            Belum ada barang. <Link to="/intake" className="text-blue-600 font-medium">Tambah pertama</Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {recent.map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.id}`}
                className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                data-testid={`recent-service-${s.ticket_no}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-slate-500">{s.ticket_no}</span>
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getStatusDisplay(s).cls}`}
                    >
                      {getStatusDisplay(s).label}
                    </span>
                  </div>
                  <div className="mt-1 font-medium text-slate-900 truncate">{s.customer_name}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {s.item_brand} {s.item_type} · {formatDate(s.created_at)}
                  </div>
                </div>
                <div className="text-right ml-3 shrink-0">
                  {showRevenue && (
                    <div className="font-semibold text-slate-800 text-sm">
                      {formatRp(s.final_cost || s.estimated_cost)}
                    </div>
                  )}
                  {!showRevenue && (
                    <div className="text-xs text-slate-400">{s.item_type}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
